"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.singleTenantDomainRedirectInputSchema = exports.singleTenantRedirectUrlSchema = exports.singleTenantEmailDomainSchema = exports.parseOrThrowClientError = exports.enumSchemaFromDynamicArray = exports.ssoProviderNameSchema = exports.SSO_PROVIDER_NAME_REGEX = exports.monthYearStringSchema = exports.monthYearPeriodNullableSchema = exports.monthYearPeriodSchema = exports.monthYearSchema = exports.vendorSchema = exports.notSpecifiedTransform = exports.dollarsToCentsSchema = exports.emailSchema = exports.uuidSchema = exports.monthSchema = exports.yearSchema = exports.intSchema = exports.positiveIntSchema = exports.nonNegativeNumberSchema = exports.isoDateTimeStringSchema = exports.dateTimeOptionalTimeZoneStringSchema = exports.dateTimeOptionalTimeZoneRegex = exports.dateCoercionSchema = exports.dateStringSchema = exports.datetimeSchema = exports.commaDelimitedStringSchema = exports.nullableOptionalStringSchema = exports.nullableStringSchema = exports.stringSchema = exports.jsonArraySchema = exports.jsonObjectSchema = exports.jsonSchema = exports.assertNeverType = exports.assertNever = void 0;
const zod_1 = __importDefault(require("zod"));
const errors_1 = require("./errors");
const money_utils_1 = require("./money.utils");
const assertNever = (value) => {
    throw (0, errors_1.createError)('ImpossibleValueError', `Unexpected code path with value: ${value}`);
};
exports.assertNever = assertNever;
const assertNeverType = (typeValue, typeName = 'type') => {
    throw new TypeError(`Unexpected ${typeName}: ${typeValue}`);
};
exports.assertNeverType = assertNeverType;
const literalSchema = zod_1.default.union([zod_1.default.string(), zod_1.default.number(), zod_1.default.boolean(), zod_1.default.null(), zod_1.default.date()]);
exports.jsonSchema = zod_1.default.lazy(() => zod_1.default.union([literalSchema, zod_1.default.array(exports.jsonSchema), zod_1.default.record(zod_1.default.string(), exports.jsonSchema)]));
exports.jsonObjectSchema = zod_1.default.record(zod_1.default.string(), exports.jsonSchema);
exports.jsonArraySchema = exports.jsonSchema.array();
exports.stringSchema = zod_1.default.string().min(1);
exports.nullableStringSchema = zod_1.default.preprocess((arg) => {
    if (typeof arg === 'string' && arg.length === 0) {
        return null;
    }
    return arg;
}, exports.stringSchema.nullable());
exports.nullableOptionalStringSchema = exports.nullableStringSchema.optional();
/**
 * Parse a comma-delimited string into an array of strings. Trims whitespace from each value. Allows empty strings.
 */
exports.commaDelimitedStringSchema = zod_1.default.preprocess((arg) => {
    // If the argument is a string, split it by comma and trim each value
    if (typeof arg === 'string') {
        return arg.split(',').map((value) => value.trim());
    }
    // Otherwise, return the argument unchanged so it can be validated by the schema
    return arg;
}, zod_1.default.array(zod_1.default.string()));
exports.datetimeSchema = zod_1.default.preprocess((arg) => {
    if (typeof arg == 'string' || arg instanceof Date) {
        return new Date(arg);
    }
}, zod_1.default.date());
const dateRegex = /\d{4}-(0\d|1[0-2])-([0-2]\d|3[01])/;
exports.dateStringSchema = zod_1.default.preprocess((arg) => {
    if (typeof arg == 'string') {
        return arg.slice(0, 10);
    }
}, exports.stringSchema.regex(dateRegex));
exports.dateCoercionSchema = zod_1.default.coerce.date();
exports.dateTimeOptionalTimeZoneRegex = /\d{4}-?(0\d|1[0-2])-?([0-2]\d|3[01])T\d\d:?\d\d:?\d\d(.\d\d\d)?([Z]|[+-]\d\d:?\d\d)?/;
exports.dateTimeOptionalTimeZoneStringSchema = exports.stringSchema.regex(exports.dateTimeOptionalTimeZoneRegex);
/**
 * Schema that validates and converts datetime strings to ISO format for external API consumption.
 * Accepts various datetime formats and ensures output is a valid ISO string.
 */
exports.isoDateTimeStringSchema = zod_1.default.preprocess((arg) => {
    if (!arg || typeof arg !== 'string') {
        throw new zod_1.default.ZodError([
            {
                code: 'custom',
                message: 'Missing or invalid datetime value',
                path: [],
            },
        ]);
    }
    try {
        const date = new Date(arg);
        if (isNaN(date.getTime())) {
            throw new zod_1.default.ZodError([
                {
                    code: 'custom',
                    message: `Invalid datetime format: ${arg}`,
                    path: [],
                },
            ]);
        }
        return date.toISOString();
    }
    catch (error) {
        if (error instanceof zod_1.default.ZodError) {
            throw error;
        }
        throw new zod_1.default.ZodError([
            {
                code: 'custom',
                message: `Failed to parse datetime: ${arg}`,
                path: [],
            },
        ]);
    }
}, exports.stringSchema);
exports.nonNegativeNumberSchema = zod_1.default.number().nonnegative();
exports.positiveIntSchema = exports.nonNegativeNumberSchema.int();
exports.intSchema = zod_1.default.int();
exports.yearSchema = zod_1.default.number().gte(1900).lte(9999);
exports.monthSchema = zod_1.default.number().gte(0).lte(11);
exports.uuidSchema = exports.stringSchema.uuid();
exports.emailSchema = zod_1.default.email();
exports.dollarsToCentsSchema = zod_1.default.preprocess((arg) => {
    if (typeof arg == 'number') {
        return (0, money_utils_1.parseCents)(arg * 100);
    }
}, zod_1.default.number());
const notSpecifiedTransform = (name) => {
    if (!name || name.length === 0 || name === 'Not Specified') {
        return 'Not Specified';
    }
    return name;
};
exports.notSpecifiedTransform = notSpecifiedTransform;
exports.vendorSchema = zod_1.default.preprocess((arg) => {
    if (typeof arg == 'string') {
        return (0, exports.notSpecifiedTransform)(arg);
    }
}, zod_1.default.string());
exports.monthYearSchema = zod_1.default.object({
    month: exports.monthSchema,
    year: exports.yearSchema,
});
exports.monthYearPeriodSchema = zod_1.default.object({
    startMonthYear: exports.monthYearSchema,
    endMonthYear: exports.monthYearSchema,
});
exports.monthYearPeriodNullableSchema = zod_1.default.object({
    startMonthYear: exports.monthYearSchema.nullable(),
    endMonthYear: exports.monthYearSchema.nullable(),
});
exports.monthYearStringSchema = zod_1.default.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);
exports.SSO_PROVIDER_NAME_REGEX = /^(oidc\.|saml\.)/;
exports.ssoProviderNameSchema = zod_1.default.string().regex(exports.SSO_PROVIDER_NAME_REGEX, "Must start with 'oidc.' or 'saml.'");
/** Intended to be used with dynamic arrays only. Static arrays are easily created with `z.enum`. */
const enumSchemaFromDynamicArray = (array) => {
    if (array.length === 0) {
        throw new Error('Array must contain at least one element to create a Zod enum');
    }
    return zod_1.default.enum([array[0], ...array.slice(1)]);
};
exports.enumSchemaFromDynamicArray = enumSchemaFromDynamicArray;
const parseOrThrowClientError = (schema, value) => {
    const parsed = schema.safeParse(value);
    if (!parsed.success) {
        const errors = parsed.error.issues.map((zodError) => ({
            code: zodError.code.toUpperCase(),
            description: `${zodError.message} at ${zodError.path.join('.')} field`,
        }));
        throw (0, errors_1.createClientErrors)(errors);
    }
    return parsed.data;
};
exports.parseOrThrowClientError = parseOrThrowClientError;
const isHttpUrl = (value) => {
    try {
        const { protocol } = new URL(value);
        return protocol === 'https:' || protocol === 'http:';
    }
    catch {
        return false;
    }
};
/** Labels of letters/digits/hyphens (not starting/ending with a hyphen), at least one dot + a TLD. */
const SINGLE_TENANT_DOMAIN_REGEX = /^([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/;
/** Validates an email domain (e.g. "acme.com") for a single-tenant redirect; trims + lowercases. */
exports.singleTenantEmailDomainSchema = zod_1.default
    .string()
    .transform((value) => value.trim().toLowerCase())
    .refine((value) => value.length > 0, 'Domain is required.')
    .refine((value) => !value.includes('@'), 'Enter just the domain, not an email address (e.g. acme.com).')
    .refine((value) => SINGLE_TENANT_DOMAIN_REGEX.test(value), 'Enter a valid domain, e.g. acme.com.');
/** Validates a single-tenant instance redirect URL; must be a full http(s) URL. */
exports.singleTenantRedirectUrlSchema = zod_1.default
    .string()
    .transform((value) => value.trim())
    .refine((value) => value.length > 0, 'Redirect URL is required.')
    .refine((value) => isHttpUrl(value), 'Enter a full URL, e.g. https://acme.neo.tax.');
/** Combined input schema for creating/updating a single-tenant domain redirect. */
exports.singleTenantDomainRedirectInputSchema = zod_1.default.object({
    emailDomain: exports.singleTenantEmailDomainSchema,
    redirectUrl: exports.singleTenantRedirectUrlSchema,
});
